import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../api/client';
import { WORKFLOW_STAGES as STAGE_KEYS, STAGE_COLORS, STAGE_LABELS, normalizeStage } from '../lib/stages';

// Shared canonical 8-stage list (mutable array copy for indexOf/map usage).
const WORKFLOW_STAGES: string[] = [...STAGE_KEYS];

export default function Cases() {
  const navigate = useNavigate();
  const [cases, setCases] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [stageFilter, setStageFilter] = useState('');
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Load from opportunities that have been promoted to cases (have lifecycle)
      const [oppRes, lcRes] = await Promise.all([
        api.get<any>('/opportunities?page_size=200'),
        api.get<any>('/lifecycle'),
      ]);
      const opps = oppRes.items || [];
      const lifecycles = lcRes.items || [];

      // Merge: show opportunities that have a lifecycle (active procurements)
      const lcMap: Record<string, any> = {};
      lifecycles.forEach((lc: any) => { lcMap[lc.opportunity_id] = lc; });

      // Stage precedence: the backend-derived stage (reflects the opportunity's
      // real status, e.g. 'awarded' -> award) wins; then the lifecycle's stage;
      // then intake. This keeps the board in sync with the opportunity even when
      // the lifecycle record is missing or stale.
      const stageOrder = WORKFLOW_STAGES;
      const mostAdvanced = (a: string, b: string) =>
        (stageOrder.indexOf(a) >= stageOrder.indexOf(b) ? a : b);

      const merged = opps
        .filter((o: any) => lcMap[o.id] || o.derived_stage)
        .map((o: any) => {
          const lcStage = normalizeStage(lcMap[o.id]?.current_stage);
          const derived = normalizeStage(o.derived_stage);
          let stage = 'intake';
          if (lcMap[o.id]?.current_stage && o.derived_stage) stage = mostAdvanced(lcStage, derived);
          else stage = o.derived_stage ? derived : (lcMap[o.id]?.current_stage ? lcStage : 'intake');
          return {
            ...o,
            lifecycle: lcMap[o.id],
            current_stage: stage,
            lc_status: lcMap[o.id]?.status || o.status || 'active',
          };
        });

      setCases(merged);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  // Filter
  let filtered = cases;
  if (stageFilter) filtered = filtered.filter(c => c.current_stage === stageFilter);
  if (search) {
    const s = search.toLowerCase();
    filtered = filtered.filter(c =>
      (c.title || '').toLowerCase().includes(s) ||
      (c.agency || '').toLowerCase().includes(s) ||
      (c.notice_id || '').toLowerCase().includes(s)
    );
  }

  // Stage counts for summary
  const stageCounts: Record<string, number> = {};
  WORKFLOW_STAGES.forEach(s => { stageCounts[s] = 0; });
  cases.forEach(c => {
    const stage = c.current_stage || 'intake';
    if (stageCounts[stage] !== undefined) stageCounts[stage]++;
  });

  return (
    <div style={{ maxWidth: 1200 }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <h2 style={{ fontSize: 20, margin: 0 }}>Active Procurements</h2>
          <p style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>All procurement cases and their lifecycle stage. Click to open workspace.</p>
        </div>
      </div>

      {/* Stage summary cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(8, 1fr)', gap: 8, marginBottom: 20 }}>
        {WORKFLOW_STAGES.map(stage => {
          const s = STAGE_COLORS[stage] || STAGE_COLORS.intake;
          const isActive = stageFilter === stage;
          return (
            <div
              key={stage}
              onClick={() => setStageFilter(stageFilter === stage ? '' : stage)}
              style={{
                padding: '12px 8px', borderRadius: 8, textAlign: 'center', cursor: 'pointer',
                background: isActive ? s.color : s.bg,
                border: `2px solid ${isActive ? s.color : 'transparent'}`,
                color: isActive ? 'white' : s.color,
                transition: 'all 0.15s',
              }}
            >
              <div style={{ fontSize: 20, fontWeight: 700 }}>{stageCounts[stage]}</div>
              <div style={{ fontSize: 10, fontWeight: 600, marginTop: 4 }}>{STAGE_LABELS[stage]}</div>
            </div>
          );
        })}
      </div>

      {/* Search */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <input
          style={{ flex: 1, padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13 }}
          placeholder="Search by title, agency, notice ID..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          aria-label="Search procurements"
        />
        {(stageFilter || search) && (
          <button onClick={() => { setStageFilter(''); setSearch(''); }} style={{ padding: '8px 12px', background: '#fee2e2', color: '#dc2626', border: '1px solid #fca5a5', borderRadius: 6, fontSize: 12, cursor: 'pointer' }}>Clear</button>
        )}
        <span style={{ padding: '8px 0', fontSize: 12, color: '#6b7280' }}>{filtered.length} procurements</span>
      </div>

      {/* Table */}
      {loading ? (
        <div style={{ padding: 32, textAlign: 'center', color: '#6b7280' }}>Loading...</div>
      ) : filtered.length === 0 ? (
        <div style={{ padding: 48, textAlign: 'center', background: 'white', borderRadius: 8, border: '1px solid #e5e7eb' }}>
          <div style={{ fontSize: 32, marginBottom: 12 }}>📋</div>
          <p style={{ color: '#6b7280', fontSize: 14 }}>No active procurements{stageFilter ? ` in ${STAGE_LABELS[stageFilter]}` : ''}.</p>
          <Link to="/opportunities" style={{ color: '#2563eb', fontSize: 13 }}>Open an opportunity to start a procurement →</Link>
        </div>
      ) : (
        <div style={{ background: 'white', borderRadius: 8, border: '1px solid #e5e7eb', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f9fafb' }}>
                <th style={thStyle}>Title</th>
                <th style={thStyle}>Agency</th>
                <th style={thStyle}>Stage</th>
                <th style={thStyle}>Progress</th>
                <th style={thStyle}>Value</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c: any) => {
                const stageIdx = WORKFLOW_STAGES.indexOf(c.current_stage || 'intake');
                const stageColor = STAGE_COLORS[c.current_stage] || STAGE_COLORS.intake;
                return (
                  <tr key={c.id} style={{ borderBottom: '1px solid #f3f4f6', cursor: 'pointer' }}
                    onClick={() => navigate(`/opportunities/${c.id}`)}
                    onMouseEnter={e => (e.currentTarget.style.background = '#f9fafb')}
                    onMouseLeave={e => (e.currentTarget.style.background = '')}
                  >
                    <td style={tdStyle}>
                      <div style={{ fontSize: 13, fontWeight: 500, color: '#1f2937' }}>{c.title || 'Untitled'}</div>
                      {c.notice_id && <div style={{ fontSize: 10, color: '#6b7280', fontFamily: 'monospace', marginTop: 2 }}>{c.notice_id}</div>}
                    </td>
                    <td style={{ ...tdStyle, fontSize: 12, color: '#6b7280' }}>{c.agency || '—'}</td>
                    <td style={tdStyle}>
                      <span style={{ fontSize: 10, padding: '3px 8px', background: stageColor.bg, color: stageColor.color, borderRadius: 9999, fontWeight: 600, whiteSpace: 'nowrap' }}>
                        {STAGE_LABELS[c.current_stage] || c.current_stage}
                      </span>
                    </td>
                    <td style={tdStyle}>
                      <div style={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                        {WORKFLOW_STAGES.map((s, i) => (
                          <div key={s} style={{ width: 14, height: 5, borderRadius: 2, background: i < stageIdx ? '#10b981' : i === stageIdx ? '#2563eb' : '#e5e7eb' }} />
                        ))}
                      </div>
                    </td>
                    <td style={{ ...tdStyle, fontSize: 12, color: '#374151' }}>{c.budget || c.estimated_value || '—'}</td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>
                      <Link to={`/opportunities/${c.id}`} onClick={e => e.stopPropagation()} style={{ padding: '4px 10px', background: '#2563eb', color: 'white', borderRadius: 4, fontSize: 11, textDecoration: 'none', fontWeight: 500 }}>
                        Open Workspace
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const thStyle: React.CSSProperties = { textAlign: 'left', padding: '10px 12px', fontSize: 11, fontWeight: 600, color: '#374151' };
const tdStyle: React.CSSProperties = { padding: '10px 12px' };
